import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { memo, useContext } from 'react';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import { getHeroBloksFromBody } from '../../utils/getHeroBloksFromBody/getHeroBloksFromBody.util';
import { getNonHeroBloksFromBody } from '../../utils/getNonHeroBloksFromBody/getNonHeroBloksFromBody.util';
import BreadcrumbBar from '../BreadcrumbBar/BreadcrumbBar';
import LiveChatWidget from '../QuoteStagePages/QuoteStageBloks/QuoteMoveSpecialist/QuoteMoveSpecialistAvailableContactMethods/LiveChat/LiveChatWidget';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';

interface PageBlok extends Blok {
  body?: Blok[] | null;
  show_share_page_bar?: boolean;
  show_contact_bar: boolean;
  show_chat?: string;
  structured_data: Blok[];
  lead_source_reference?: string;
}

interface PageProps {
  blok: PageBlok;
}

const Page: React.FC<PageProps> = ({ blok }) => {
  const { header, footer, contact_bar, share_page_bar, breadcrumbs } =
    useContext(LayoutContext);

  const heroBloks: Blok[] = getHeroBloksFromBody(blok.body || []);
  const nonHeroBloks: Blok[] = getNonHeroBloksFromBody(blok.body || []);

  if (!Cookies.get('lead_source_reference') && blok?.lead_source_reference) {
    Cookies.set('lead_source_reference', blok.lead_source_reference, {
      expires: 7,
    });
  }

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired:
          blok.component !== 'search_results_page',
        showContactBar: blok.show_contact_bar,
      }}
    >
      {/*-- structured data --*/}
      {blok.structured_data?.map((structuredData: Blok) => (
        <StoryblokComponent blok={structuredData} key={structuredData._uid} />
      ))}

      {/*-- visible content --*/}
      <div className="flex flex-col h-screen justify-between">
        {header && (
          <StoryblokComponent blok={header.content} key={header._uid} />
        )}
        <main className="mb-auto" {...storyblokEditable(blok)}>
          {heroBloks.map((heroBlok: Blok) => (
            <StoryblokComponent blok={heroBlok} key={heroBlok._uid} />
          ))}
          {breadcrumbs && <BreadcrumbBar breadcrumbs={breadcrumbs} />}
          {nonHeroBloks.map((nonHeroBlok: Blok) => (
            <StoryblokComponent blok={nonHeroBlok} key={nonHeroBlok._uid} />
          ))}
          {blok.show_share_page_bar && share_page_bar && (
            <StoryblokComponent
              blok={{
                ...share_page_bar.content,
                needsThinSpan: blok.component === 'article',
              }}
              key={share_page_bar._uid}
            />
          )}
          {blok.show_contact_bar && contact_bar && (
            <StoryblokComponent
              blok={contact_bar.content}
              key={contact_bar._uid}
            />
          )}

          {blok?.show_chat && <LiveChatWidget engagement_id={blok.show_chat} />}
        </main>
        {footer && (
          <StoryblokComponent blok={footer.content} key={footer._uid} />
        )}
      </div>
      <TestingGrid />
    </PageContext.Provider>
  );
};

export default memo(Page);
